import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { BillingOrder } from 'src/app/models/billingorder.model';
import { UserService } from 'src/app/services/user.service';
import { OrderTransaction, PendingOrderService } from 'src/app/services/pendingorder.service';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { ScrBaseComponent } from 'src/app/components/base.component';
import { Lease } from 'src/app/models/lease.model';
import { Aggregator, ConfigService } from 'src/app/services/config.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { defaults } from 'lodash-es';
import { ApplicationType, CampaignType, VetStatus, OrderTransactionType, PartnerType } from 'src/app/models/constants';
import { Account } from 'src/app/models/account.model';
import { AccountService } from 'src/app/services/account.service';
import { Address } from 'src/app/services/address-book.service';
import { handleEnterKeyPress } from 'src/app/shared/common-events';
import { first } from 'rxjs';
import { IncomingState } from '../order-short-code.component';
import { PartnerService } from '../../../../services/partner.service';
import { MultiSelect, MultiSelectChangeEvent } from 'primeng/multiselect';
import { Message } from '../../../../shared/messages/messages.component';

@Component({
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.scss']
})

export class ApplicationComponent extends ScrBaseComponent implements OnInit, OnDestroy {
  aggregators: Aggregator[] = [];
  account?: Account;
  pendingOrder = new BillingOrder();
  leaseApplicationData = new Lease();
  maxSelectedLabels = 5;
  regRequired = false;
  orderTransactionType!: OrderTransactionType;

  public needsactionRenew = false;
  public campaignTypeChoice?: CampaignType;
  public legalEntityTypes: SelectItem<string | null>[] = [];
  public publicEmailDomains: string[] = [];

  // workaround for robot framework radios.
  public codeType = '';

  public brandOptions!: SelectItem<number>[];
  validBrandIds!: number[];
  public contentProviderOptions!: SelectItem<number>[];
  validContentProviderIds!: number[];
  partnersValidated = false;
  brandError?: Message;

  campaignTypeOptions = [
    { label: 'Federal', value: 'Federal' },
    { label: 'Local', value: 'Local' },
    { label: 'Other', value: 'Other' },
    { label: 'State', value: 'State' },
    { label: 'Tribal', value: 'Tribal' }
  ];


  @ViewChild(ConfirmDialog, { static: false }) confirmDialog!: ConfirmDialog;
  @ViewChild('application', { static: false }) form!: NgForm;

  constructor(
    private userService: UserService,
    private configService: ConfigService,
    private partnerService: PartnerService,
    protected pendOrderService: PendingOrderService,
    protected confirmationService: ConfirmationService,
    protected router: Router,
    protected accountService: AccountService
  ) {
    super();
  }

  ngOnInit(): void {

    this.initPartnerOptions();

    this.configService.getConfig().pipe(first()).subscribe(cfg => {
      this.legalEntityTypes = cfg.legalEntityTypes.map(et => {
        return { label: et, value: et, disabled: false };
      });
      this.legalEntityTypes.unshift({ label: '', value: null, disabled: false });
      this.publicEmailDomains = cfg.publicEmailDomains;
    });

    const incomingState = history.state as IncomingState;

    this.needsactionRenew = incomingState.needsactionRenew === true;

    this.orderTransactionType = this.getOrderTransactionType();

    if (this.orderTransactionType !== OrderTransactionType.New) {
      this.pendingOrder = this.pendOrderService.orderTransaction$.value.transactionOrder;
      this.leaseApplicationData = this.pendingOrder.leases[0];
      if (!this.leaseApplicationData.brands) {
        this.leaseApplicationData.brands = [];
      }
      if (!this.leaseApplicationData.brandIds) {
        this.leaseApplicationData.brandIds = this.leaseApplicationData.brands.map(b => b.partnerId);
      }
      this.validatePartnerIds();
      if (!this.leaseApplicationData.forPoliticalCampaign) {
        this.leaseApplicationData.fecId = '';
        this.leaseApplicationData.politicalCampaignType = undefined;
        this.leaseApplicationData.politicalCampaignInfo = '';
      }
      else {
        if (!this.leaseApplicationData.politicalCampaignType && this.leaseApplicationData.forPoliticalCampaign) {
          this.campaignTypeChoice = CampaignType.Federal;
          this.leaseApplicationData.politicalCampaignType = CampaignType.Federal;
          this.leaseApplicationData.politicalCampaignInfo = '';
        } else {
          this.campaignTypeChoice = this.leaseApplicationData.politicalCampaignType;
          if (this.leaseApplicationData.politicalCampaignType === 'Federal')
            this.leaseApplicationData.politicalCampaignInfo = '';
          else {
            this.leaseApplicationData.fecId = '';
          }
        }
      }

      // must track the "original" value when we first load the page so we know how
      // to enable/disable the parked and demo radio buttons on a renew.
      if (this.leaseApplicationData.originalDemoCode === undefined) {
        this.leaseApplicationData.originalDemoCode = this.leaseApplicationData.demoCode;
      }
      if (this.leaseApplicationData.originalParkedCode === undefined) {
        this.leaseApplicationData.originalParkedCode = this.leaseApplicationData.parkedCode;
      }

      if (this.leaseApplicationData.demoCode) {
        this.codeType = 'demo';
      } else if (this.leaseApplicationData.parkedCode) {
        this.codeType = 'parked';
      } else {
        this.codeType = 'active';
      }
      this.configService.getAggregators().subscribe(res => { this.aggregators = res; });
    }
    else {
      if (this.orderTransactionType === OrderTransactionType.New) { //between navigation or new order
        this.pendingOrder = this.pendOrderService.getPendingOrder();
        this.leaseApplicationData = this.pendOrderService.getLeaseApplication();
        if (this.leaseApplicationData) {

          if (!this.leaseApplicationData.brands) {
            this.leaseApplicationData.brands = [];
          }
          if (!this.leaseApplicationData.brandIds) {
            this.leaseApplicationData.brandIds = this.leaseApplicationData.brands.map(b => b.partnerId);
          }
          this.validatePartnerIds();
          // default the parked short code value.
          if (this.leaseApplicationData.parkedCode === undefined) {
            this.leaseApplicationData.parkedCode = false;
          }

          if (!this.leaseApplicationData.forPoliticalCampaign) {
            this.leaseApplicationData.fecId = '';
            this.leaseApplicationData.politicalCampaignType = undefined;
            this.leaseApplicationData.politicalCampaignInfo = '';
          }
          else {
            if (!this.leaseApplicationData.politicalCampaignType && this.leaseApplicationData.forPoliticalCampaign) {
              this.campaignTypeChoice = CampaignType.Federal;
              this.leaseApplicationData.politicalCampaignType = CampaignType.Federal;
              this.leaseApplicationData.politicalCampaignInfo = '';
            } else {
              this.campaignTypeChoice = this.leaseApplicationData.politicalCampaignType;
              if (this.leaseApplicationData.politicalCampaignType === CampaignType.Federal)
                this.leaseApplicationData.politicalCampaignInfo = '';
              else
                this.leaseApplicationData.fecId = '';

            }
          }

        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.forkedObserve<any>([
          this.userService.getUserProfile(),
          this.configService.getAggregators(),
          this.accountService.getAccount()
        ], res => {
          this.leaseApplicationData.firstName = this.leaseApplicationData.firstName ?
            this.leaseApplicationData.firstName : res[0].user.firstName;

          this.leaseApplicationData.lastName = this.leaseApplicationData.lastName ?
            this.leaseApplicationData.lastName : res[0].user.lastName;

          this.leaseApplicationData.phone = this.leaseApplicationData.phone ?
            this.leaseApplicationData.phone : res[0].user.phone;

          this.leaseApplicationData.phoneExtension = this.leaseApplicationData.phoneExtension ?
            this.leaseApplicationData.phoneExtension : res[0].user.phoneExtension;

          this.leaseApplicationData.email = this.leaseApplicationData.email ?
            this.leaseApplicationData.email : res[0].user.email;

          this.leaseApplicationData.country = this.leaseApplicationData.country ?
            this.leaseApplicationData.country : res[0].user.country;

          this.regRequired = this.leaseApplicationData.country === 'us' || this.leaseApplicationData.country === 'ca';

          this.leaseApplicationData.address1 = this.leaseApplicationData.address1 ?
            this.leaseApplicationData.address1 : res[0].user.address1;

          this.leaseApplicationData.address2 = this.leaseApplicationData.address2 ?
            this.leaseApplicationData.address2 : res[0].user.address2;

          this.leaseApplicationData.city = this.leaseApplicationData.city ?
            this.leaseApplicationData.city : res[0].user.city;

          this.leaseApplicationData.state = this.leaseApplicationData.state ?
            this.leaseApplicationData.state : res[0].user.state;

          if (!this.leaseApplicationData.zip) {
            this.leaseApplicationData.zip = res[0].user.zip;
          }

          this.leaseApplicationData.accountId = this.pendingOrder.accountId = res[2].account?.accountId;
          this.leaseApplicationData.company = this.pendingOrder.company = res[2].account?.company;

          this.aggregators = res[1];

          this.leaseApplicationData.forPoliticalCampaign = this.leaseApplicationData.forPoliticalCampaign ?
            this.leaseApplicationData.forPoliticalCampaign : false;

          if (res[2].account) {
            this.account = res[2].account;
            if (this.pendingOrder) {
              this.pendingOrder.accountId = this.account?.accountId;
            }
          }

          if (this.leaseApplicationData.demoCode) {
            this.codeType = 'demo';
          } else if (this.leaseApplicationData.parkedCode) {
            this.codeType = 'parked';
          } else {
            this.codeType = 'active';
          }

          if (!this.leaseApplicationData.aggregatorId) {
            setTimeout(() => {
              this.leaseApplicationData.aggregatorId = this.account?.defaultAggregatorId;
            }, 0);
          }
          if (!this.leaseApplicationData.applicationProvider) {
            this.leaseApplicationData.applicationProvider = this.account?.defaultApplicationProvider;
          }
          return false;
        });
      }
    }
  }

  get usagesEnabled(): string[] {
    // tricky for vanilla renew.  we could be returning to this tab
    // after navigating around.  The restriction needs to be based on
    // the original parked/demo state.
    if (this.orderTransactionType === OrderTransactionType.New) {
      return ['active', 'demo', 'parked'];
    }
    else if (this.orderTransactionType === OrderTransactionType.Renew ||
      this.orderTransactionType === OrderTransactionType.NewPayLater ||
      this.orderTransactionType === OrderTransactionType.RenewPayLater) {
      if (this.leaseApplicationData.originalParkedCode === true) {
        return ['active', 'demo', 'parked'];
      }
      else if (this.leaseApplicationData.originalDemoCode === true) {
        return ['active', 'demo'];
      }
      else {
        return ['active'];
      }
    }
    else {
      return ['active'];
    }
  }

  /**
   * Validate the partner IDs in the lease application data with those loaded from the backend.
   * TODO: also validate the content provider.
   */
  private validatePartnerIds() {
    if (this.validBrandIds && this.leaseApplicationData.brandIds) {
      if (this.partnersValidated) {
        return;
      }
      const badOnes = (this.leaseApplicationData.brands ?? []).filter(b => {
        return !this.validBrandIds.includes(b.partnerId);
      });
      if (badOnes.length > 0) {
        // filter the brands and brandIds.
        this.leaseApplicationData.brandIds = (this.leaseApplicationData.brandIds ?? []).filter(b => !badOnes.map(bb => bb.partnerId).includes(b));
        this.leaseApplicationData.brands = (this.leaseApplicationData.brands ?? []).filter(b => !badOnes.map(bb => bb.partnerId).includes(b.partnerId));
        // create a sticky error at the top of the page.
        const names = badOnes.map(b => b.company).join(', ');
        const m = new Message();
        m.title = 'Warning';
        m.dismissible = true;
        m.text = `The Brand Client(s): ${names} cannot be included because their vet status is no longer Successful. You can either continue without those Brand Client(s) or wait until their vetting process is complete.`;
        this.brandError = m;
      }

      // also check the content provider.
      if (this.validContentProviderIds && this.leaseApplicationData.cpId) {
        if (!this.validContentProviderIds.includes(this.leaseApplicationData.cpId)) {
          this.leaseApplicationData.cpId = undefined;
        }
      }


      this.partnersValidated = true;
    }
  }

  // When we are leaving this page
  override ngOnDestroy(): void {
    super.ngOnDestroy();
    //Navigating back and forth internally in order tab - //Should never be empty when doing the three order tabs
    if (!this.isNewOrder()) {
      this.pendingOrder.leases = this.pendingOrder.leases.map(lease => {
        defaults(lease, this.leaseApplicationData);
        return lease;
      });
      this.pendOrderService.setOrderTransactionDetails(this.getOrderTransactionType(), this.pendingOrder);
      return;
    }
    //NonNewOrders Navigates away from order tab all together,reset it to  new order type in parent but pendingOrder is set to empty here)
    if (this.orderTransactionType === OrderTransactionType.New && (
      this.pendingOrder.applicationType === ApplicationType.Renew ||
      this.pendingOrder.orderId !== null && this.pendingOrder.orderId !== undefined)) {
      this.pendingOrder = new BillingOrder();
      this.pendingOrder.accountId = this.account?.accountId;
      this.leaseApplicationData = new Lease();
    } else {
      if (this.pendOrderService.keepTheCode) {
        const ot = new OrderTransaction(this.pendingOrder, OrderTransactionType.New);
        this.pendOrderService.emitChangePendOrder(ot);
        this.pendOrderService.emitChangeLeaseApplication(this.leaseApplicationData);
      } else {
        this.pendingOrder = new BillingOrder();
        this.pendingOrder.accountId = this.account?.accountId;
        this.leaseApplicationData = new Lease();
      }
    }
  }

  initPartnerOptions(): void {

    this.observe(this.partnerService.retrievePartners(VetStatus.Successful), (rsp) => {
      if (rsp.partners) {
        const brands = rsp.partners.filter(p=>p.partnerType === PartnerType.Brand);
        const cps = rsp.partners.filter(p=>p.partnerType === PartnerType.ContentProvider);
        this.validBrandIds = brands.map(p => p.partnerId);
        this.brandOptions = brands.map(b => { return { 'label': b.company, 'value': b.partnerId }; });
        this.validContentProviderIds = cps.map(p => p.partnerId);
        this.contentProviderOptions = cps.map(b => { return { 'label': b.company, 'value': b.partnerId }; });
      }
      else {
        this.validBrandIds = [];
        this.validContentProviderIds = [];
      }
      this.validatePartnerIds();
      return false;
    });
  }

  getRegionRequired(): boolean {
    return this.leaseApplicationData.country === 'us' || this.leaseApplicationData.country === 'ca';
  }

  next({ form }: NgForm): void {
    //In some cases we get the aggregator Id like from prepay order or renew
    //but those aggregators are not active, force the user to choose the aggregator
    if (!this.leaseApplicationData.forPoliticalCampaign) {
      this.leaseApplicationData.politicalCampaignType = undefined;
      this.leaseApplicationData.politicalCampaignInfo = undefined;
    }

    if (this.leaseApplicationData.politicalCampaignInfo)
      this.leaseApplicationData.politicalCampaignInfo = this.leaseApplicationData.politicalCampaignInfo.trim();

    const aggFound = this.aggregators.findIndex(agg => agg.aggregatorId === this.leaseApplicationData.aggregatorId);
    if (aggFound == -1) {
      this.leaseApplicationData.aggregatorId = undefined;
      form.controls['aggregator'].setErrors({ required: true });
    }

    document.getElementById('application-form')?.classList.remove('presubmit');
    form.markAllAsTouched();
    for (const name in form.controls) {
      form.controls[name].markAsDirty();
    }

    if (form.invalid) {
      this.scrollToFirstError();
    }
    else {
      const transType = this.getOrderTransactionType();
      if (this.isNewOrder()) {
        this.pendOrderService.emitChangeLeaseApplication(this.leaseApplicationData);
        const ot = new OrderTransaction(this.pendingOrder, OrderTransactionType.New);
        this.pendOrderService.emitChangePendOrder(ot);
      }

      const props =
        ['firstName', 'lastName', 'phone', 'phoneExtension', 'email', 'country', 'address1', 'address2', 'city', 'state', 'zip',
          'cpId', 'brands', 'brandIds'
        ];
      // apply the lease application data to each lease in the order.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataAsAny = this.leaseApplicationData as any;
      this.pendingOrder.leases.forEach(lease => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const alease = lease as any;
        for (const p of props) {
          alease[p] = dataAsAny[p];
        }

        lease.parkedCode = this.leaseApplicationData.parkedCode;
        lease.demoCode = this.leaseApplicationData.demoCode;
        lease.aggregatorId = this.leaseApplicationData.aggregatorId;
        lease.applicationProvider = this.leaseApplicationData.applicationProvider;
        lease.fecId = this.leaseApplicationData.fecId;
        lease.forPoliticalCampaign = this.leaseApplicationData.forPoliticalCampaign;
        lease.politicalCampaignInfo = this.leaseApplicationData.politicalCampaignInfo;
        lease.politicalCampaignType = this.leaseApplicationData.politicalCampaignType;

        if (this.leaseApplicationData.forPoliticalCampaign && this.leaseApplicationData.politicalCampaignType === CampaignType.Federal)
          lease.politicalCampaignInfo = '';
        else if (this.leaseApplicationData.forPoliticalCampaign && this.leaseApplicationData.politicalCampaignType !== CampaignType.Federal)
          lease.fecId = '';
        else {
          lease.politicalCampaignInfo = '';
          lease.fecId = '';
          lease.politicalCampaignType = undefined;

        }
      });
      this.pendOrderService.setOrderTransactionDetails(transType, this.pendingOrder);
      this.router.navigate(
        ['/dashboard',
          {
            outlets: {
              'navbar-tab':
                ['order-code',
                  { outlets: { 'order-code-tab': ['payment'] } }
                ]
            }
          }
        ], { state: { renew: true, needsactionRenew: this.needsactionRenew } });

      window.scrollTo(0, 0);
    }
  }


  cancelOrder(): void {
    if (!this.isNewOrder()) this.simplyCancel(); //Handles paylater and renew too
    else this.cancelNewOrder();
  }
  simplyCancel(): void {
    this.pendOrderService.leavingCheckoutPages();
    if (this.needsactionRenew) {
      setTimeout(() => {
        this.router.navigate(['/dashboard', { outlets: { 'navbar-tab': ['my-shortcodes', { outlets: { 'my-shortcode-tab': ['shortcode-needs-action'] } }] } }]);
      }, 500);
    } else {
      setTimeout(() => {
        this.router.navigate(['/dashboard', { outlets: { 'navbar-tab': ['my-shortcodes', { outlets: { 'my-shortcode-tab': ['shortcode-list'] } }] } }]);
      }, 500);
    }

  }
  cancelNewOrder(): void {
    this.confirmationService.confirm({
      message: `<p>Please confirm that you want to empty your cart and cancel this order.</p>`,
      header: 'Clear Order?',
      key: 'canOrder',
      accept: () => {
        this.confirmDialog.unbindGlobalListeners();
        this.pendingOrder = new BillingOrder();
        this.pendingOrder.accountId = this.account?.accountId;
        this.leaseApplicationData = new Lease();
        this.pendOrderService.removeAllOrder(OrderTransactionType.New);
        this.pendOrderService.saveOrder(false);
        this.pendOrderService.leavingCheckoutPages();
        setTimeout(() => {
          this.router.navigate([
            '/dashboard',
            {
              outlets: {
                'navbar-tab': [
                  'order-code',
                  { outlets: { 'order-code-tab': ['cart'] } },
                ],
              },
            },
          ]);
        }, 500);
      },
      reject: () => {
        this.confirmDialog.unbindGlobalListeners();
      }
    });
  }


  isPayLater(): boolean {
    let plFlag = false;
    const saveType = this.getOrderTransactionType();

    plFlag = saveType === OrderTransactionType.NewPayLater ||
      saveType === OrderTransactionType.RenewPayLater ||
      saveType === OrderTransactionType.Renew1Plus12PayLater ||
      saveType === OrderTransactionType.Renew1Plus12AfterDeadline;

    return plFlag;
  }
  isRenew(): boolean {
    let renew = false;
    const saveType = this.getOrderTransactionType();
    renew = saveType === OrderTransactionType.Renew ||
      saveType === OrderTransactionType.RenewPayLater ||
      saveType === OrderTransactionType.Renew1Plus12PayLater ||
      saveType === OrderTransactionType.EnrollInMonthlyBilling ||
      saveType === OrderTransactionType.Renew1Plus12 ||
      saveType === OrderTransactionType.Renew1Plus12AfterDeadline;
    return renew;
  }
  isRenewIntoMB(): boolean {
    const transType = this.getOrderTransactionType();
    return transType === OrderTransactionType.EnrollInMonthlyBilling || transType === OrderTransactionType.Renew1Plus12;
  }

  isRenewDirectIntoMB(): boolean {
    const transType = this.getOrderTransactionType();
    return transType === OrderTransactionType.EnrollInMonthlyBilling;
  }

  isNewOrder(): boolean {
    let newFlag = false;
    const transType = this.getOrderTransactionType();
    newFlag = transType === OrderTransactionType.New;
    return newFlag;
  }
  getOrderTransactionType(): OrderTransactionType {
    return this.pendOrderService.orderTransaction$.value.transactionType;
  }

  loadApplicantAddress(address: Address) {
    this.leaseApplicationData.firstName = address.firstName;
    this.leaseApplicationData.lastName = address.lastName;
    this.leaseApplicationData.phone = address.phone;
    this.leaseApplicationData.phoneExtension = address.phoneExtension;
    this.leaseApplicationData.email = address.email;
    this.leaseApplicationData.country = address.country;
    this.leaseApplicationData.address1 = address.address1;
    this.leaseApplicationData.address2 = address.address2;
    this.leaseApplicationData.city = address.city;
    this.leaseApplicationData.state = address.state;
    this.leaseApplicationData.zip = address.zip;
  }


  handleEnter(event: Event) {
    handleEnterKeyPress(event);
  }


  selectCampaignType() {
    this.leaseApplicationData.forPoliticalCampaign = true;
    if (this.leaseApplicationData.politicalCampaignType) {
      this.campaignTypeChoice = this.leaseApplicationData.politicalCampaignType;
    }
    else {
      this.campaignTypeChoice = CampaignType.Federal;
      this.leaseApplicationData.politicalCampaignType = CampaignType.Federal;
    }

    if (this.leaseApplicationData.politicalCampaignType === CampaignType.Federal)
      this.leaseApplicationData.politicalCampaignInfo = '';
    else
      this.leaseApplicationData.fecId = '';


  }

  onChangeCampaignType(campaignTypeChoice?: CampaignType) {
    this.leaseApplicationData.politicalCampaignType = campaignTypeChoice;

    if (this.leaseApplicationData.politicalCampaignType === CampaignType.Federal)
      this.leaseApplicationData.politicalCampaignInfo = '';
    else
      this.leaseApplicationData.fecId = '';
  }

  clearFields() {
    this.leaseApplicationData.fecId = '';
    this.campaignTypeChoice = undefined;
    this.leaseApplicationData.politicalCampaignInfo = '';
    this.leaseApplicationData.politicalCampaignType = undefined;

  }

  /**
   * This is a workaround for a primeng bug where the max selected labels does not
   * work when the display is in chip mode.   Return chip mode normally but comma
   * mode when the "<n> Brands Selected" is to be displayed.
   */
  get multiSelectDisplay() {
    return (this.leaseApplicationData?.brandIds?.length ?? 0) > this.maxSelectedLabels ? 'comma' : 'chip';
  }

  /**
   * A brand was selected.  Not sure if we need to do anything...
   * @param e - selection event
   */
  brandSelected(m: MultiSelect, e: MultiSelectChangeEvent) {
    this.brandError = undefined;

    // e.value is an array of brandIds.
    // e.itemValue is the brandId and label.
    if (e.value.length > 50) {
      // reject it.
      let sel: number[] = e.value ?? [];
      sel = sel.filter(v => v !== e.itemValue?.value);
      m.updateModel(sel);
    }
  }

  setCodeType(codeType: string): void {
    this.codeType = codeType;
    if (codeType === 'active') {
      this.leaseApplicationData.demoCode = false;
      this.leaseApplicationData.parkedCode = false;
    }
    if (codeType === 'demo') {
      this.leaseApplicationData.demoCode = true;
      this.leaseApplicationData.parkedCode = false;
      this.leaseApplicationData.brandIds = [];
    }
    if (codeType === 'parked') {
      this.leaseApplicationData.demoCode = false;
      this.leaseApplicationData.parkedCode = true;
      this.leaseApplicationData.brandIds = [];
    }
  }
}


